import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseNumericLimitArgs {
	/** Lower bound (inclusive). Omit for no lower bound. */
	min?: number;
	/** Upper bound (inclusive). Omit for no upper bound. */
	max?: number;
}

/** Which bound a clamp hit — drives the one-shot pulse on the matching adornment / stepper button. */
export type LimitPulse = 'min' | 'max' | null;

export interface UseNumericLimitResult {
	/** Clamp `raw` into `[min, max]`. When the value is actually capped, fires the one-shot `pulse`. */
	clamp: (raw: number) => number;
	/** The bound the last clamp hit (`'min'` / `'max'`), or `null`. Auto-clears ~400ms after firing. */
	pulse: LimitPulse;
}

/** How long the one-shot pulse stays set before it auto-clears (ms). */
const PULSE_DURATION_MS = 400;

/**
 * useNumericLimit
 *
 * Pure-ish numeric clamping with a one-shot "you hit the bound" pulse. No DOM, no form context —
 * a building block for `PunchupNumericInput` (and any other limit-aware numeric control). `clamp`
 * returns the value forced into `[min, max]`; when it actually capped the input it sets `pulse` to
 * the bound that was hit. `pulse` auto-clears after ~400ms via a `setTimeout` in a `useEffect`, so a
 * consumer can drive a discrete flash animation and an `aria-live` announcement off a single signal.
 *
 * Co-located with the other primitive hooks (mirrors `usePopoverDismiss/`).
 */
export const useNumericLimit = ({ min, max }: UseNumericLimitArgs): UseNumericLimitResult => {
	const [pulse, setPulse] = useState<LimitPulse>(null);
	// A monotonically increasing key so two consecutive clamps to the SAME bound still re-arm the
	// timeout (and let the consumer re-trigger the one-shot animation) — see the effect below.
	const pulseSeq = useRef(0);
	const [pulseTick, setPulseTick] = useState(0);

	const clamp = useCallback(
		(raw: number): number => {
			let next = raw;
			let hit: LimitPulse = null;
			if (max !== undefined && next > max) {
				next = max;
				hit = 'max';
			}
			// min after max so a single value below min in an inverted/degenerate range still resolves
			// to min (min wins), matching the "floor" intent of the consumers.
			if (min !== undefined && next < min) {
				next = min;
				hit = 'min';
			}
			if (hit) {
				pulseSeq.current += 1;
				setPulse(hit);
				setPulseTick(pulseSeq.current);
			}
			return next;
		},
		[min, max]
	);

	// One-shot: clear the pulse ~400ms after it fires. Keyed on pulseTick (not pulse) so a repeat
	// clamp to the same bound re-arms the timer instead of being swallowed by an equal state value.
	useEffect(() => {
		if (pulse === null) return;
		const timer = setTimeout(() => setPulse(null), PULSE_DURATION_MS);
		return () => clearTimeout(timer);
	}, [pulse, pulseTick]);

	return { clamp, pulse };
};
